import React, { useEffect, useState } from 'react';
import { get, isEmpty } from 'lodash';
import { useForm } from 'react-hook-form';
import ReviewOutlinedSVG from 'src/assets/svg/review-outlined.svg';
import {
  INPUT_ACTION_TYPES,
  ITEM_TYPES
} from 'src/componentsTailwind/CircleItems/CircleItems';
import { CircleItems } from 'src/componentsTailwind/index';
import { BADGE_COLOR_CLASSES } from 'src/componentsTailwind/tailwindConstants';
import { queryClient } from 'src/index';
import GoalUpdateFlyout from 'src/pagesDashboard/GoalSettings/components/GoalUpdateFlyout';
import { useAccounts } from 'src/queries/account';
import { QUERY_KEYS as GOAL_QUERY_KEYS, useGoalUpdate } from 'src/queries/goal';
import { useTree } from 'src/queries/tree';
import CircledIcon from 'src/components/CircledIcon/CircledIcon';
import {
  appUtils, Base, Select, toast
} from 'src/components/index';
import { getGoalStatuses } from 'src/containers/UserProfile/Goals/constants';
import COMMON_CONSTANTS from 'common/commonConstants';
import commonDateUtils from 'common/commonDateUtils';
import commonGoalUtils from 'common/commonGoalUtils';
import commonViewPermissions from 'common/commonViewPermissions';
import STYLE from 'src/constants/style';

const { getGoalStatusValueByKey, getGoalStatusKeyByValue, GOALS } = COMMON_CONSTANTS;

const GoalUpdatesFeed = ({ goal, isLoading = false }) => {
  const updates = get(goal, 'updates', []).sort(
    (a, b) => b.timestamp - a.timestamp
  );

  const [flyoutGoalUpdate, setFlyoutGoalUpdate] = useState(updates[0]);

  const [isGoalUpdateFlyoutOpen, setIsGoalUpdateFlyoutOpen] = useState(false);

  const {
    data: { tree },
    isFetching: isFetchingTree,
    isError: isErrorTree
  } = useTree();

  const {
    data: authorAccounts,
    isFetching: isFetchingAuthorAccounts,
    isError: isErrorAuthorAccounts
  } = useAccounts(
    {
      ids: updates.map((u) => [u.author, u.latestAuthor]).flat()
    },
    {
      projection: ['name', 'imageUrl'],
      page: { size: Number.MAX_SAFE_INTEGER }
    }
  );

  const openGoalUpdateFlyout = (updateId) => {
    setFlyoutGoalUpdate(() => {
      const selectedUpdate = updates.find(
        (u) => u._id === updateId || u.id === updateId
      );
      if (selectedUpdate) {
        setIsGoalUpdateFlyoutOpen(true);
        return selectedUpdate;
      }
    });
  };

  const statusOptions = getGoalStatuses();

  const { watch, reset, setValue } = useForm({
    defaultValues: {
      text: '',
      progress: commonGoalUtils.getGoalProgressOption(goal),
      status: commonGoalUtils.getGoalStatusOptionFromLatestUpdate(
        goal,
        statusOptions
      )
    }
  });

  const formValues = watch();
  const { text, progress, status } = formValues;
  const resetForm = () => {
    const defaultValues = {
      text: '',
      progress: commonGoalUtils.getGoalProgressOption(goal),
      status: commonGoalUtils.getGoalStatusOptionFromLatestUpdate(
        goal,
        statusOptions
      )
    };
    reset(defaultValues);
  };

  useEffect(() => {
    resetForm();
  }, [updates]);

  const { mutateAsync: updateGoal, isLoading: isLoadingGoalUpdate } = useGoalUpdate(goal.id);

  const isFetching = isFetchingAuthorAccounts || isFetchingTree;
  const isError = isErrorAuthorAccounts || isErrorTree;
  const isReady = tree && tree.id && !isFetching && !isError;

  const hasUpdates = get(goal, 'updates', []).length > 0;
  if (!isReady) {
    return null;
  }

  const isBinary = goal.type === GOALS.TYPES.BINARY;
  const isPercent = goal.type === GOALS.TYPES.PERCENT;

  const loggedUser = appUtils.getLoggedUser();
  const { companyid } = loggedUser;
  const getStatusLabel = (s) => getGoalStatusValueByKey(getGoalStatusKeyByValue(s), companyid);
  const getStatusColorClasses = (s) => {
    let colorClasses = BADGE_COLOR_CLASSES.BRIGHT_GRAY;

    if (s === GOALS.STATUS.DRAFT) colorClasses = BADGE_COLOR_CLASSES.BRIGHT_GRAY;
    if (s === GOALS.STATUS.NOT_STARTED) colorClasses = BADGE_COLOR_CLASSES.BRIGHT_PURPLE;
    if (s === GOALS.STATUS.ON_HOLD) colorClasses = BADGE_COLOR_CLASSES.BRIGHT_RED;
    if (s === GOALS.STATUS.ON_TRACK) colorClasses = BADGE_COLOR_CLASSES.BRIGHT_GREEN;
    if (s === GOALS.STATUS.OFF_TRACK) colorClasses = BADGE_COLOR_CLASSES.BRIGHT_GOLD;
    if (s === GOALS.STATUS.COMPLETE) colorClasses = BADGE_COLOR_CLASSES.BRIGHT_BLUE;

    return colorClasses;
  };

  const addUpdate = async () => {
    try {
      const loggedUserId = appUtils.getLoggedUserId();

      const payload = {
        text,
        author: loggedUserId,
        timestamp: commonDateUtils.getUnixDateNow(),
        status: status.value,
        progress: get(progress, 'value', progress)
      };

      const response = await updateGoal({ updates: [payload] });

      if (!response || !response.success) {
        return toast.error('Try again!');
      }

      if (response.warning) {
        const message = get(
          response,
          'message',
          'Failed to add update. Try again later!'
        );
        toast.closeAll();
        return toast.show(message);
      }

      toast.show('Update added!');

      await queryClient.refetchQueries([GOAL_QUERY_KEYS.GOALS, goal.id]);
      queryClient.invalidateQueries(GOAL_QUERY_KEYS.GOALS);
      queryClient.invalidateQueries(GOAL_QUERY_KEYS.GOALS_LOOKUP);
      queryClient.invalidateQueries(GOAL_QUERY_KEYS.GOAL_STATISTICS);

      resetForm();
    } catch (error) {
      return toast.error(error ?? 'Try again!');
    }
  };

  const loggedUserId = appUtils.getLoggedUserId();
  const canManageGoal = commonViewPermissions.canManageGoal(
    tree,
    loggedUserId,
    goal.assignee
  );

  const renderProgressField = () => {
    if (isBinary) {
      const options = [
        { value: 0, label: 'Not done' },
        { value: 1, label: 'Done' }
      ];

      return (
        <Select
          classes='w-[120px]'
          optionsWidth='fit'
          maxOptionWidth='500px'
          placeholder='Any progress?'
          options={options}
          title={get(progress, 'label')}
          disabled={isLoadingGoalUpdate}
          onChange={(option) => setValue('progress', option)}
        />
      );
    }

    return (
      <input
        className='p-2 border border-mid-gray bg-white rounded-lg resize-none w-60'
        placeholder='Any progress?'
        type='number'
        min={isPercent ? 0 : undefined}
        max={isPercent ? 100 : undefined}
        value={progress}
        disabled={isLoadingGoalUpdate}
        onChange={(e) => setValue('progress', e.target.value)}
      />
    );
  };

  const getIsDataValid = () => {
    if (isEmpty(text) || text.length < 10) {
      return false;
    }

    if (isEmpty(status.value)) {
      return false;
    }

    if (isBinary) {
      if (!isFinite(progress.value)) {
        return false;
      }
    } else if (!isFinite(progress)) {
      return false;
    }

    if (isPercent && (progress < 0 || progress > 100)) {
      return false;
    }

    return true;
  };

  const buildUpdateItems = () => {
    if (hasUpdates) {
      return updates.map((u) => {
        const authorAccount = authorAccounts.find((a) => a.id === u.author);
        const latestAuthorAccount = authorAccounts.find(
          (a) => a.id === u.latestAuthor
        );

        const statusLabel = getStatusLabel(u.status);
        const colorClasses = getStatusColorClasses(u.status);

        return {
          type: ITEM_TYPES.TEXT,
          id: u.id ?? u._id,
          label: authorAccount.name,
          underlabel: (
            <div className='w-full flex justify-between'>
              <span className='w-full flex flex-col'>
                <span className='flex gap-2'>
                  <p className='m-0 whitespace-nowrap'>{`Shared ${commonDateUtils.unixToTimeAgo(u.timestamp)}`}</p>
                  {canManageGoal ? (
                    <button
                      onClick={() => openGoalUpdateFlyout(u._id || u.id)}
                      className='focus:outline-none'
                    >
                      <p className='m-0 whitespace-nowrap !text-purple'>
                        View Details
                      </p>
                    </button>
                  ) : null}
                </span>
                {u.latestTimestamp > u.timestamp ? (
                  <p className='m-0 whitespace-nowrap'>{`Edited ${commonDateUtils.unixToTimeAgo(u.latestTimestamp)} by ${latestAuthorAccount.name}`}</p>
                ) : null}
              </span>
              <span
                className={`tooltip rounded-md px-2 py-[2px] ring-1 ring-inset w-[100px] h-fit ${colorClasses}`}
              >
                <div className='m-0 leading-4 text-xs font-medium text-center'>
                  {statusLabel}
                </div>
              </span>
            </div>
          ),
          text: u.text,
          imageSrc: appUtils.getProfilePictureSrc(authorAccount.imageUrl)
        };
      });
    }

    if (!isLoading) {
      return [
        {
          type: ITEM_TYPES.TEXT,
          label: 'WorkStory',
          underlabel: <p className='italic'>Nothing here yet!</p>,
          text: '<p className="italic">Once this goal received updates, they will be displayed here.</p>',
          imageSrc: appUtils.getProfilePictureSrc()
        }
      ];
    }

    return [];
  };

  const items = [
    ...(canManageGoal
      ? [
        {
          type: ITEM_TYPES.INPUT,
          imageSrc: appUtils.getProfilePictureSrc(loggedUser.imageUrl),
          value: text,
          placeholder: 'Add an update',
          disabled: isLoadingGoalUpdate,
          onChange: (value) => setValue('text', value),
          id: 'write-update',
          actions: [
            {
              type: INPUT_ACTION_TYPES.CUSTOM,
              align: 'left',
              render: renderProgressField
            },
            {
              type: INPUT_ACTION_TYPES.SELECT,
              align: 'left',
              options: statusOptions,
              label: get(status, 'label'),
              loading: isLoadingGoalUpdate,
              onChange: (option) => setValue('status', option),
              id: 'select-status',
              drop: updates.length >= 3 ? 'down' : 'up'
            },
            {
              type: INPUT_ACTION_TYPES.SUBMIT,
              align: 'right',
              label: 'Submit update',
              variant: 'black-outline',
              disabled: isLoadingGoalUpdate || !getIsDataValid(),
              onClick: async () => {
                await addUpdate();
              }
            }
          ]
        }
      ]
      : []),
    ...buildUpdateItems()
  ];

  return (
    <>
      <GoalUpdateFlyout
        width={40}
        isOpen={isGoalUpdateFlyoutOpen}
        setIsOpen={setIsGoalUpdateFlyoutOpen}
        update={flyoutGoalUpdate}
        goalId={goal.id}
        showXButton={false}
      />
      <Base
        classes={`${STYLE.CONTAINER_WHITE} !p-4`}
        loading={isLoading || isLoadingGoalUpdate}
        relative
      >
        <CircledIcon svg={<ReviewOutlinedSVG className='text-purple' />} />
        <p className='text-xl text-black font-bold mb-0 mr-8'>Goal Updates</p>
        <p className='m-0 text-sm font-normal mt-2 mb-4 leading-5 text-bluish-gray'>
          Status and progress updates shared regarding this goal
        </p>
        <div className='border-b border-mid-gray my-3 transform -translate-x-[16px] w-[calc(100%+32px)]' />
        <CircleItems items={items} />
      </Base>
    </>
  );
};

export default GoalUpdatesFeed;
